import { useEffect, useState, useCallback } from 'react';

export type ThemeName = 'light' | 'dark' | 'compact';

export interface Theme {
  name: ThemeName;
  label: string;
  path: string;
}

export const themes: Theme[] = [
  { name: 'light', label: 'Light', path: '/themes/light.css' },
  { name: 'dark', label: 'Dark', path: '/themes/dark.css' },
  { name: 'compact', label: 'Compact', path: '/themes/compact.css' },
];

const THEME_STORAGE_KEY = 'ancana-theme';
const THEME_LINK_ID = 'theme-stylesheet';

const useTheme = (): {
  currentTheme: ThemeName;
  setTheme: (theme: ThemeName) => Promise<void>;
  loading: boolean;
  themes: Theme[];
} => {
  const [currentTheme, setCurrentTheme] = useState<ThemeName>('light');
  const [loading, setLoading] = useState<boolean>(false);

  // Load saved theme on mount
  useEffect(() => {
    // Import setTwoToneColor from @ant-design/icons
    import('@ant-design/icons').then(({ setTwoToneColor }) => {
      const savedTheme = (localStorage.getItem(THEME_STORAGE_KEY) as ThemeName) || 'light';
      setCurrentTheme(savedTheme);
      
      // Configure TwoTone icon colors based on theme
      if (savedTheme === 'dark') {
        // For dark theme, set both primary and secondary colors
        setTwoToneColor(['#c49662', '#333333']); // [primaryColor, secondaryColor]
      } else {
        setTwoToneColor('#c49662'); // Reset to default primary color
      }
      
      // Apply the saved theme immediately if not default
      if (savedTheme !== 'light') {
        const theme = themes.find(t => t.name === savedTheme);
        if (theme) {
          loadThemeCSS(theme.path).catch(console.error);
        }
      }
    });
  }, []);

  const loadThemeCSS = (themePath: string): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if theme link already exists
      let themeLink = document.getElementById(THEME_LINK_ID) as HTMLLinkElement;
      
      if (!themeLink) {
        // Create new link element
        themeLink = document.createElement('link');
        themeLink.id = THEME_LINK_ID;
        themeLink.rel = 'stylesheet';
        document.head.appendChild(themeLink);
      }

      // Create a new link element for smooth transition
      const newLink = document.createElement('link');
      newLink.rel = 'stylesheet';
      newLink.href = themePath;
      
      // Set up load and error handlers
      newLink.onload = () => {
        // Once new theme is loaded, replace the old one
        if (themeLink && themeLink.parentNode) {
          themeLink.parentNode.replaceChild(newLink, themeLink);
          newLink.id = THEME_LINK_ID;
        }
        resolve();
      };
      
      newLink.onerror = () => {
        reject(new Error(`Failed to load theme: ${themePath}`));
      };

      // Start loading the new theme
      // Insert it temporarily to trigger loading
      document.head.appendChild(newLink);
      
      // If it's the same href, just resolve
      if (themeLink.href === newLink.href) {
        document.head.removeChild(newLink);
        resolve();
      }
    });
  };

  const setTheme = useCallback(async (themeName: ThemeName) => {
    const theme = themes.find(t => t.name === themeName);
    if (!theme) return;

    setLoading(true);
    
    try {
      // Load the theme CSS
      await loadThemeCSS(theme.path);
      
      // Configure TwoTone icon colors based on theme
      const { setTwoToneColor } = await import('@ant-design/icons');
      if (themeName === 'dark') {
        // For dark theme, set both primary and secondary colors
        setTwoToneColor(['#c49662', '#333333']); // [primaryColor, secondaryColor]
      } else {
        setTwoToneColor('#c49662'); // Reset to default primary color
      }
      
      // Update state and localStorage
      setCurrentTheme(themeName);
      localStorage.setItem(THEME_STORAGE_KEY, themeName);
      
      // Optional: Add theme class to body for additional customization
      document.body.className = themeName === 'dark' ? 'theme-dark' : 
                                themeName === 'compact' ? 'theme-compact' : 
                                'theme-light';
      
    } catch (error) {
      console.error('Failed to switch theme:', error);
      throw error;
    } finally {
      setLoading(false);
    }
  }, []);

  return {
    currentTheme,
    setTheme,
    loading,
    themes
  };
};

export default useTheme;